import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, ErrorNotice, Icon, StatusDot, StatusPill } from '@hive/ui';
import { api, errorMessage, type Alert } from '../api/client';
import { useLive, useLoad, useNow } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { Shell } from '../components/Shell';
import { ago } from '../format';

const KIND: Record<Alert['kind'], string> = {
  network_outage: 'Network outage',
  venue_offline: 'Venue offline',
  screen_offline: 'Screen offline',
  qoe_degraded: 'Picture quality',
  stream_failing: 'Stream failing',
  licence_expiring: 'Licence',
  rights_expiring: 'Rights',
};

type Tab = 'open' | 'mine' | 'resolved';

/** Alerts for real venues: who is on what, and what happened. */
export function AlertsPage() {
  const { user } = useAuth();
  const now = useNow(15_000);
  const [tab, setTab] = useState<Tab>('open');
  const [kind, setKind] = useState<Alert['kind'] | 'all'>('all');
  const [error, setError] = useState<string | null>(null);
  const open = useLive(async () => (await api.GET('/v1/alerts')).data, [], 15_000);
  const resolved = useLoad(async () => (await api.GET('/v1/alerts', { params: { query: { status: 'resolved', limit: 200 } } })).data, [tab]);
  const people = useLoad(async () => (await api.GET('/v1/alerts/assignees')).data);

  const list = useMemo(() => {
    const src = tab === 'resolved' ? resolved.data ?? [] : open.data ?? [];
    return src.filter((a) => (tab !== 'mine' || a.assignee?.id === user?.id) && (kind === 'all' || a.kind === kind));
  }, [tab, kind, open.data, resolved.data, user?.id]);
  const mine = (open.data ?? []).filter((a) => a.assignee?.id === user?.id).length;

  async function act(a: Alert, action: 'acknowledge' | 'resolve') {
    setError(null);
    const path = action === 'acknowledge' ? '/v1/alerts/{alertId}/acknowledge' : '/v1/alerts/{alertId}/resolve';
    const { error: err } = await api.POST(path, { params: { path: { alertId: a.alertId! } }, body: {} });
    if (err) return setError(errorMessage(err));
    void open.refresh();
    if (action === 'resolve') void resolved.reload();
  }

  async function assign(a: Alert, userId: string) {
    setError(null);
    const { error: err } = await api.POST('/v1/alerts/{alertId}/assign', { params: { path: { alertId: a.alertId! } }, body: { userId: userId || null } });
    if (err) return setError(errorMessage(err));
    void open.refresh();
  }

  return (
    <Shell network={null} live={open.live}>
      <main className="mc-page">
        <div className="mc-pagehead">
          <div className="mc-pagehead__left">
            <h1 className="mc-h1">Alerts</h1>
            <span className="mc-pill-soft">Real venues · simulated ones stay on the map</span>
          </div>
          <div className="mc-pagehead__actions">
            <Link to="/integrations" className="hv-btn hv-btn--secondary hv-btn--sm">
              Who gets notified
            </Link>
          </div>
        </div>
        {error && <ErrorNotice title={error} />}

        <Card className="mc-table-card">
          <div className="mc-toolbar">
            <div className="mc-tabs" role="tablist" aria-label="Alerts">
              {(
                [
                  ['open', 'Open', open.data?.length],
                  ['mine', 'Assigned to me', mine],
                  ['resolved', 'Resolved (30 days)', undefined],
                ] as [Tab, string, number | undefined][]
              ).map(([key, label, n]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} className={`mc-tab${tab === key ? ' mc-tab--on' : ''}`} onClick={() => setTab(key)}>
                  {label}
                  {n !== undefined && <span className="mc-tab__count">{n}</span>}
                </button>
              ))}
            </div>
            <label className="mc-select">
              <span className="hv-sr-only">Kind</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option value="all">Every kind</option>
                {Object.entries(KIND).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <table className="mc-table">
            <thead>
              <tr>
                <th scope="col">Alert</th>
                <th scope="col">Where</th>
                <th scope="col">{tab === 'resolved' ? 'Resolved' : 'Since'}</th>
                <th scope="col">{tab === 'resolved' ? 'By' : 'Stage'}</th>
                <th scope="col">Owner</th>
                {tab !== 'resolved' && <th scope="col" aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.alertId ?? a.id}>
                  <td>
                    <span className="mc-alert-cell">
                      <StatusDot status={a.severity === 'critical' ? 'offline' : 'degraded'} />
                      <span className="mc-table__who">
                        <span className="mc-strong">{a.message}</span>
                        <span className="mc-small hv-muted">
                          {KIND[a.kind]}
                          {a.status === 'acknowledged' ? ` · acknowledged by ${a.acknowledgedBy}` : ''}
                          {a.note ? ` · “${a.note}”` : ''}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="mc-small">
                    {a.screenId ? (
                      <Link to={`/screen/${a.screenId}`}>{a.screenName}</Link>
                    ) : a.venueId ? (
                      <Link to={`/venue/${a.venueId}`}>{a.venueName}</Link>
                    ) : (
                      'Several venues'
                    )}
                    {a.screenId && a.venueName && <span className="hv-muted mc-line">{a.venueName}</span>}
                  </td>
                  <td className="mc-small">
                    {tab === 'resolved' ? (a.resolvedAt ? `${ago(a.resolvedAt, now)} ago` : '') : `${ago(a.since ?? a.openedAt, now)}`}
                  </td>
                  <td className="mc-small">
                    {tab === 'resolved' ? a.resolvedBy : a.stageLabel ? <StatusPill status={a.stage && a.stage >= 3 ? 'offline' : a.stage === 2 ? 'expiring' : 'idle'}>{a.stageLabel}</StatusPill> : '—'}
                  </td>
                  <td className="mc-small">
                    {tab === 'resolved' ? (
                      a.assignee?.name ?? '—'
                    ) : (
                      <label className="mc-select mc-select--inline">
                        <span className="hv-sr-only">Assign</span>
                        <select value={a.assignee?.id ?? ''} onChange={(e) => assign(a, e.target.value)}>
                          <option value="">Unassigned</option>
                          {(people.data ?? []).map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </td>
                  {tab !== 'resolved' && (
                    <td className="mc-nowrap">
                      {a.status === 'open' && (
                        <Button variant="link" onClick={() => act(a, 'acknowledge')}>
                          Acknowledge
                        </Button>
                      )}
                      <Button variant="link" onClick={() => act(a, 'resolve')}>
                        Resolve
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {(tab === 'resolved' ? resolved.data : open.data) && list.length === 0 && (
                <tr>
                  <td colSpan={6} className="mc-table__empty">
                    <Icon name="check" size={16} /> {tab === 'resolved' ? 'Nothing resolved in the last 30 days.' : 'Nothing needs attention.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
        <p className="mc-small hv-muted">
          Screen alerts open after about 25 seconds without a heartbeat, then escalate at 15 minutes, an hour, and when a player&apos;s playback
          lease is 4 hours from running out. Acknowledging stops further escalation notices. Resolving by hand closes the alert; it reopens if the
          problem is still there.
        </p>
      </main>
    </Shell>
  );
}
